import type { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { EMPTY_OBJECT } from '../../../../utils';
import { baseColumn } from '../baseColumn';
import type { ColDefFactory, Column } from '../types';
import { dateStringComparator } from '../utils';
import {
  getPredictionEventForSecurity,
  getPredictionSecurityFromRow,
  resolvePredictionEventSeries,
  type PredictionMarketColumnParams,
} from './resolvePredictionMarketFields';

export type { PredictionMarketColumnParams };

/** Category for a prediction-outcome symbol, resolved from blotter prediction event/series context. */
export const predictionCategory: ColDefFactory<Column<PredictionMarketColumnParams>> = column => ({
  ...baseColumn(column),
  colId: column.id ?? 'predictionCategory',
  valueGetter: (params: ValueGetterParams) => {
    const columnParams = column.params ?? EMPTY_OBJECT;
    const security = getPredictionSecurityFromRow(params.data, params.context?.current, columnParams);
    const eventsByID = params.context?.current?.predictionEventsByID;
    const event = security && eventsByID ? getPredictionEventForSecurity(security, eventsByID) : undefined;
    const seriesByID = params.context?.current?.predictionEventSeriesByID;
    const series = seriesByID ? resolvePredictionEventSeries(event, seriesByID) : undefined;
    const category = series?.Category ?? event?.Category;
    return category?.trim() || '';
  },
});

/** Series name for a prediction-outcome symbol, resolved from blotter prediction event/series context. */
export const predictionSeries: ColDefFactory<Column<PredictionMarketColumnParams>> = column => ({
  ...baseColumn(column),
  colId: column.id ?? 'predictionSeries',
  valueGetter: (params: ValueGetterParams) => {
    const columnParams = column.params ?? EMPTY_OBJECT;
    const security = getPredictionSecurityFromRow(params.data, params.context?.current, columnParams);
    const eventsByID = params.context?.current?.predictionEventsByID;
    const event = security && eventsByID ? getPredictionEventForSecurity(security, eventsByID) : undefined;
    const seriesByID = params.context?.current?.predictionEventSeriesByID;
    const series = seriesByID ? resolvePredictionEventSeries(event, seriesByID) : undefined;
    return series?.SeriesName ?? '';
  },
});

/** Event name for a prediction-outcome symbol, resolved from blotter prediction event/series context. */
export const predictionEvent: ColDefFactory<Column<PredictionMarketColumnParams>> = column => ({
  ...baseColumn(column),
  colId: column.id ?? 'predictionEvent',
  valueGetter: (params: ValueGetterParams) => {
    const columnParams = column.params ?? EMPTY_OBJECT;
    const security = getPredictionSecurityFromRow(params.data, params.context?.current, columnParams);
    const eventsByID = params.context?.current?.predictionEventsByID;
    const event = security && eventsByID ? getPredictionEventForSecurity(security, eventsByID) : undefined;
    return event?.EventName ?? '';
  },
});

/** Lightweight ISO date column for frequently updating prediction-market grids. */
export const predictionExpiry: ColDefFactory<Column> = column => ({
  ...baseColumn(column),
  colId: column.id ?? 'predictionExpiry',
  cellDataType: 'dateString',
  initialWidth: 100,
  comparator: dateStringComparator,
  valueFormatter: ({ value }: ValueFormatterParams<unknown, string>) =>
    typeof value === 'string' ? value.slice(0, 10) : '',
  sortingOrder: ['desc', 'asc'],
});
